import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import profileImg from "../assets/images/profilepic.png";
import SectionHeader from "./ui/SectionHeader";
import LazyImage from "./ui/LazyImage";
import GitHubStats from "./ui/GitHubStats";
import { cn, spacing, animations } from "../utils/cn";

const About = () => {
  const { t } = useTranslation();

  const technologies = [
    "HTML5 & CSS3",
    "JavaScript (ES6+)",
    "React (Hooks & Components)",
    "CSS Grid & Flexbox",
    "Bootstrap & Tailwind CSS",
    "Responsive Design",
    "Git & GitHub",
    "npm & Vite",
    "Vercel & Netlify",
    "TypeScript (Learning)",
    "Redux (Learning)",
    "Node.js (Basics)",
  ];

  return (
    <section id="about" className={cn("bg-navy", spacing.section)}>
      <div className={cn(spacing.container, "w-full")}>
        <motion.div {...animations.fadeInUp}>
          <SectionHeader number="01" title={t('about.title')} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-16">
            {/* Text Content */}
            <div className="lg:col-span-2">
              <div className="text-slate space-y-4 text-base sm:text-lg">
                <p>
                  {t('about.paragraph1')}
                </p>

                <p>
                  {t('about.paragraph2')}
                </p>

                <p>
                  {t('about.paragraph3')}
                </p>

                <p>
                  {t('about.techStack')}
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 mt-5 text-sm font-mono">
                  {technologies.map((tech, index) => (
                    <li key={index} className="flex items-center py-1">
                      <span className="text-accent mr-2">▹</span>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Profile Image */}
            <div className="lg:col-span-1">
              <motion.div
                className="relative group max-w-xs mx-auto lg:mx-0 perspective-1000"
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: 5,
                }}
                transition={{
                  duration: 0.4,
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
                style={{
                  transformStyle: "preserve-3d",
                }}
              >
                <div className="relative transform-gpu">
                  {/* Image wrapper with accent border */}
                  <div className="relative rounded-lg border-2 border-accent bg-accent/5 overflow-hidden transition-all duration-500 group-hover:border-accent group-hover:shadow-2xl group-hover:shadow-accent/20">
                    <LazyImage
                      src={profileImg}
                      alt="Anthony Njeri"
                      className="relative rounded-lg object-cover w-full aspect-square"
                      fallbackColor="#8b5cf6"
                    />
                    {/* Gradient overlay with sophisticated animation */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-secondary/60 via-secondary/40 to-accent/30 rounded-lg"
                      whileHover={{
                        opacity: 0,
                        background:
                          "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                      }}
                      transition={{ duration: 0.5 }}
                    />

                    {/* Shimmer effect */}
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100"
                      initial={false}
                      whileHover={{
                        background:
                          "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)",
                        x: ["-100%", "100%"],
                      }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    />
                  </div>

                  {/* Floating accent box with 3D effect */}
                  <motion.div
                    className="absolute inset-0 rounded-lg border-2 border-accent/60 -z-10"
                    style={{
                      transform:
                        "translateX(16px) translateY(16px) translateZ(-10px)",
                    }}
                    whileHover={{
                      x: 8,
                      y: 8,
                      rotateY: -5,
                      rotateX: -5,
                      borderColor: "rgba(59, 130, 246, 0.8)",
                    }}
                    transition={{ duration: 0.4 }}
                  />

                  {/* Particle effects on hover */}
                  <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-accent rounded-full"
                        style={{
                          left: `${20 + i * 15}%`,
                          top: `${10 + i * 10}%`,
                        }}
                        animate={{
                          y: [0, -20, 0],
                          opacity: [0, 1, 0],
                          scale: [0, 1, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.2,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* GitHub Stats Section */}
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <GitHubStats />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
